/**
 * The rubric — criteria, extraction definitions, disqualifiers and bands
 * (PRD §3.2, ARCH §2.2).
 *
 * The rubric owns scoring. `design/screen-manifest.md` B12 asks whether
 * per-option scores belong on the block or in the rubric, because the
 * `editor-build` inspector renders them on the block; PRD §3.2 puts
 * `criteria[]` with their weights under `rubric`, and RULE-013 requires one
 * weight vocabulary for closed answers, extracted fields and transcripts
 * alike. So the block owns the OPTIONS and the rubric owns what each option is
 * WORTH. The inspector can still edit both in one panel — it writes the points
 * into the criterion.
 *
 * Point arithmetic, fixed here and depended on by scoring.ts (Story 2.2):
 *   • a criterion's `weight` IS its maximum — `points_max` in the `scores`
 *     table equals it by construction, so the two columns cannot drift
 *   • every point value in a criterion's map is an integer in `0..weight`
 *   • the score is the sum of awarded points; its ceiling is the sum of weights
 *   • bands tile `0..sum(weights)` with no gap and no overlap, so no score can
 *     ever land outside a band
 */
import { z } from 'zod'

/* ── conditions — shared by logic[] and disqualifiers[] ─────────────────── */

/**
 * What a condition can read. `answer` and `extracted` are the two halves of
 * RULE-012's modality-agnostic capture; `metadata` is PRD §3.15 attribution,
 * enumerated from the `Attribution` type in `packages/api/src/db/schema.ts`
 * rather than left open, so a typo is a validation error instead of a rule
 * that silently never fires.
 */
export const METADATA_KEYS = [
  'country',
  'region',
  'city',
  'timezone',
  'device',
  'browser',
  'referrer',
  'landing_page',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  /** PRD §3.10: evaluated from `request.cf` against `funnel_service_areas`.
   *  Values are `inside` and `outside`. Backs the manifest's second
   *  disqualifier, "Location is outside service area". */
  'service_area',
] as const

export const metadataKeySchema = z.enum(METADATA_KEYS)
export type MetadataKey = z.infer<typeof metadataKeySchema>

export const conditionSubjectSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('answer'), block_id: z.string().min(1) }),
  z.object({ kind: z.literal('extracted'), field_key: z.string().min(1) }),
  z.object({ kind: z.literal('metadata'), key: metadataKeySchema }),
])
export type ConditionSubject = z.infer<typeof conditionSubjectSchema>

export const CONDITION_OPERATORS = [
  'is',
  'is_not',
  'is_one_of',
  'is_not_one_of',
  'gt',
  'gte',
  'lt',
  'lte',
  'contains',
  'not_contains',
  'is_empty',
  'is_not_empty',
] as const

export const conditionOperatorSchema = z.enum(CONDITION_OPERATORS)
export type ConditionOperator = z.infer<typeof conditionOperatorSchema>

const NO_VALUE_OPERATORS = new Set<ConditionOperator>(['is_empty', 'is_not_empty'])
const LIST_OPERATORS = new Set<ConditionOperator>(['is_one_of', 'is_not_one_of'])
const NUMERIC_OPERATORS = new Set<ConditionOperator>(['gt', 'gte', 'lt', 'lte'])

const scalarValueSchema = z.union([z.string(), z.number(), z.boolean()])

export const conditionSchema = z
  .object({
    subject: conditionSubjectSchema,
    operator: conditionOperatorSchema,
    /** Absent for `is_empty` / `is_not_empty`; an array for the `_one_of`
     *  pair; a number for the four comparisons; a scalar otherwise. */
    value: z.union([scalarValueSchema, z.array(scalarValueSchema)]).optional(),
  })
  .superRefine((condition, ctx) => {
    const { operator, value } = condition
    if (NO_VALUE_OPERATORS.has(operator)) {
      if (value !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['value'],
          message: `operator "${operator}" takes no value — remove it`,
        })
      }
      return
    }
    if (value === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['value'],
        message: `operator "${operator}" needs a value`,
      })
      return
    }
    if (LIST_OPERATORS.has(operator)) {
      if (!Array.isArray(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['value'],
          message: `operator "${operator}" needs a list of values`,
        })
      } else if (value.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['value'],
          message: `operator "${operator}" was given an empty list, so it can never match — list at least one value, or delete the rule`,
        })
      }
    }
    if (!LIST_OPERATORS.has(operator) && Array.isArray(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['value'],
        message: `operator "${operator}" takes a single value, not a list`,
      })
    }
    if (NUMERIC_OPERATORS.has(operator) && typeof value !== 'number') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['value'],
        message: `operator "${operator}" compares numbers — give it a number`,
      })
    }
  })
export type Condition = z.infer<typeof conditionSchema>

/* ── scoring maps — what an answer is worth ─────────────────────────────── */

const points = z.number().int().min(0)

/** Option-keyed points. Keys are option ids on the referenced block; the
 *  cross-reference is checked in funnel.ts, which is the layer that can see
 *  both. Also serves `consent` with the keys `true` and `false`. */
const optionScoringSchema = z.object({
  kind: z.literal('options'),
  points_by_option: z.record(z.string().min(1), points),
  /** Awarded when the answer matches no listed option, or is blank. */
  default_points: points.default(0),
})

/** Numeric bands for `number`, `currency` (integer cents) and `scale`, checked
 *  in order: the first band whose bounds contain the value wins, and BOTH
 *  bounds are inclusive — `award` in scoring.ts tests `>= min` and `<= max`, and
 *  a band `{ min: 5, max: 5 }` matches exactly 5. `null` means unbounded on that
 *  side. Two bands that share a boundary give it to the first one. */
const rangeScoringSchema = z.object({
  kind: z.literal('ranges'),
  ranges: z
    .array(
      z.object({
        min: z.number().nullable(),
        max: z.number().nullable(),
        points,
      }),
    )
    .min(1),
  default_points: points.default(0),
})

/** PRD §3.6 `rules` mode: "Keyword/length matching. Fallback when AI is
 *  unavailable or the analysis cap is reached." First matching rule wins. */
const keywordScoringSchema = z.object({
  kind: z.literal('keywords'),
  rules: z
    .array(
      z.object({
        any_of: z.array(z.string().min(1)).min(1),
        min_length: z.number().int().min(0).optional(),
        points,
      }),
    )
    .min(1),
  default_points: points.default(0),
})

/** Answered at all, or not. The only map an `email`/`phone`/`name`/`date`
 *  block can carry without inventing semantics for its value. */
const presenceScoringSchema = z.object({
  kind: z.literal('presence'),
  points_present: points,
  points_absent: points.default(0),
})

export const scoringMapSchema = z.discriminatedUnion('kind', [
  optionScoringSchema,
  rangeScoringSchema,
  keywordScoringSchema,
  presenceScoringSchema,
])
export type ScoringMap = z.infer<typeof scoringMapSchema>

/** Every point value a map can award. Used by validation here, by the engine
 *  in Story 2.2 and by the calibration sampler's stated ranges in Story 2.4. */
export function scoringMapOutcomes(map: ScoringMap): number[] {
  switch (map.kind) {
    case 'options':
      return [...Object.values(map.points_by_option), map.default_points]
    case 'ranges':
      return [...map.ranges.map((r) => r.points), map.default_points]
    case 'keywords':
      return [...map.rules.map((r) => r.points), map.default_points]
    case 'presence':
      return [map.points_present, map.points_absent]
  }
}

export const maxPoints = (map: ScoringMap): number => Math.max(...scoringMapOutcomes(map))
export const minPoints = (map: ScoringMap): number => Math.min(...scoringMapOutcomes(map))

/* ── DECISION-031 — the multi-select clamp, said out loud ───────────────── */

/**
 * The most a MULTI-select criterion can award before the clamp.
 *
 * `maxPoints` above answers "what is the best single option worth", which is
 * the whole answer for a single-select. A multi-select sums the options the
 * respondent picked (Q-009, DECISION-031), so its worst case is picking every
 * option that carries points — and the engine then clamps that sum to the
 * criterion's weight, which is what keeps `points_awarded <= points_max` and
 * the band ceiling with it.
 *
 * Negative points are excluded rather than summed: they can only pull the
 * total DOWN, so a warning that netted them off would understate the number
 * the operator actually needs to see.
 */
export const optionSum = (map: ScoringMap): number =>
  map.kind === 'options'
    ? Object.values(map.points_by_option).reduce((sum, n) => sum + Math.max(0, n), 0)
    : maxPoints(map)

/** One criterion whose options can sum past its own weight. */
export type WeightWarning = {
  criterion_id: string
  label: string
  /** The criterion's weight — the ceiling the engine clamps to. */
  weight: number
  /** What its options add up to if every one of them is picked. */
  can_sum_to: number
}

/**
 * Which criteria will silently clamp, and by how much.
 *
 * DECISION-031, verbatim: the selected options are "**summed and clamped to the
 * criterion's weight** — as Story 2.2 shipped — **and the Qualify tab renders a
 * non-blocking warning** when a multi-select criterion's option points can sum
 * past its weight, naming both numbers. The clamp stays the runtime behaviour;
 * what changes is that the operator is told it exists before it fires."
 *
 * NON-BLOCKING, so this is not a Zod refinement. An operator assigning
 * 10 + 10 + 10 to a criterion weighted 25 has written something legal that will
 * behave differently from how it reads, and refusing to save it would be
 * refusing a rubric that scores correctly. Telling them is the whole fix.
 *
 * IT LIVES HERE, not in the tab. Story 7.1's criterion says so — "beside the
 * validation that already knows what a criterion can award; the tab renders it,
 * never recomputes it" — because a warning the UI computes is a warning the
 * bench, the API and the exports would each have to compute again, and the
 * fourth copy is the one that is wrong.
 *
 * The multi-select test arrives as a PREDICATE rather than as the funnel:
 * `selection` lives on the block and `funnel.ts` imports this module, so
 * reaching for a `Funnel` here would be a cycle. The caller knows its blocks.
 */
export function overWeightWarnings(
  criteria: Criterion[],
  isMultiSelect: (blockId: string) => boolean,
): WeightWarning[] {
  const out: WeightWarning[] = []
  for (const criterion of criteria) {
    if (criterion.source !== 'answer_ref') continue
    if (criterion.points.kind !== 'options') continue
    if (!isMultiSelect(criterion.block_id)) continue

    const canSum = optionSum(criterion.points)
    if (canSum <= criterion.weight) continue
    out.push({
      criterion_id: criterion.id,
      label: criterion.label,
      weight: criterion.weight,
      can_sum_to: canSum,
    })
  }
  return out
}

/* ── extraction definitions (PRD §3.6) ──────────────────────────────────── */

/**
 * "The operator is not saying *AI, decide if this is good.* They are saying
 * *from this paragraph, extract budget as a number and timeline as one of
 * these four values.*" (RULE-021.) Hence a named, typed field with its own
 * enum or range — never a free-form instruction.
 *
 * The object is named apart from the rule across its keys so that
 * `add_extraction_field` (`operations.ts`, Story 27.5) takes these keys and no
 * restated copy of them: a discriminated union's members must be plain objects,
 * so the operation carries the shape and checks the rule when it builds the field.
 */
export const extractionFieldObjectSchema = z.object({
  field_key: z
    .string()
    .regex(/^[a-z][a-z0-9_]{0,63}$/, 'lower_snake_case, starting with a letter'),
  /** The free-text block this reads. Must be a text block in
   *  `ai_extraction` mode — checked in funnel.ts. */
  block_id: z.string().min(1),
  label: z.string().min(1).max(200),
  type: z.enum(['string', 'number', 'boolean', 'enum', 'date']),
  enum_values: z.array(z.string().min(1)).min(2).optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  /** What to pull out, in the operator's words. Becomes the field
   *  description in the JSON schema handed to the model at M2. */
  description: z.string().min(1).max(2000),
})

export const extractionFieldSchema = extractionFieldObjectSchema
  .superRefine((field, ctx) => {
    if (field.type === 'enum' && !field.enum_values) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['enum_values'],
        message: 'an enum field needs its permitted values listed',
      })
    }
    if (field.type !== 'enum' && field.enum_values) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['enum_values'],
        message: `enum_values only applies to type "enum", not "${field.type}"`,
      })
    }
  })
export type ExtractionField = z.infer<typeof extractionFieldSchema>

/**
 * How many fields one rubric may define. Named rather than written inline
 * because the Qualify tab states it — `design/screens/editor-qualify.html`
 * draws "3 of 50 fields" — and a second literal 50 in the dashboard is the copy
 * that stops agreeing with this one (Story 20.2).
 */
export const EXTRACTION_FIELD_LIMIT = 50

/* ── criteria ───────────────────────────────────────────────────────────── */

/** PRD §3.2's order: "answer_ref | extracted | derived | metadata | transcript" (DECISION-138). */
export const CRITERION_SOURCES = ['answer_ref', 'extracted', 'derived', 'metadata', 'transcript'] as const
export const criterionSourceSchema = z.enum(CRITERION_SOURCES)
export type CriterionSource = z.infer<typeof criterionSourceSchema>

/**
 * What a `derived` criterion may read — DECISION-138, Story 20.9. A value
 * computed about the WHOLE submission rather than read out of one block, so it
 * is not an `extracted` field (which reads one question) and not `metadata`
 * (attribution, which a model's judgement does not belong in).
 *
 * CLOSED, with one member. `icp_fit` is the 0–100 semantic ICP sub-score
 * (PRD §3.8, Story 19.4). The vocabulary is closed so it cannot become a bucket
 * for anything a model emits: a second key is a decision, not an edit.
 */
export const DERIVED_KEYS = ['icp_fit'] as const
export const derivedKeySchema = z.enum(DERIVED_KEYS)
export type DerivedKey = z.infer<typeof derivedKeySchema>

/**
 * How long a criterion's or a gate's label may be. A question may run to 500 characters, and
 * both are named after the question they read, so a copied label goes through `clipLabel`.
 */
export const RUBRIC_LABEL_MAX = 200

/**
 * A label that fits the rubric: the text, or its first 199 characters and an ellipsis. For a
 * label COPIED from something longer — written as-is, a 450-character question made its
 * criterion one the save refused, at a path naming nothing the operator had typed (found by
 * Story 27.1).
 */
export function clipLabel(text: string): string {
  return text.length <= RUBRIC_LABEL_MAX ? text : `${text.slice(0, RUBRIC_LABEL_MAX - 1)}…`
}

const criterionBase = {
  id: z.string().min(1),
  /** Rendered verbatim on the evidence rows and stored on `scores`. */
  label: z.string().min(1).max(RUBRIC_LABEL_MAX),
  /** The criterion's share of the total, and its maximum. */
  weight: z.number().int().min(1).max(1000),
  points: scoringMapSchema,
}

export const criterionSchema = z.discriminatedUnion('source', [
  z.object({ ...criterionBase, source: z.literal('answer_ref'), block_id: z.string().min(1) }),
  z.object({ ...criterionBase, source: z.literal('extracted'), field_key: z.string().min(1) }),
  /**
   * The ICP fit, through points the operator sets — never added to the total on
   * its own (DECISION-138). The rubric refuses one while `icp_profile` is empty,
   * because there would be nothing to measure a lead against.
   */
  z.object({ ...criterionBase, source: z.literal('derived'), key: derivedKeySchema }),
  z.object({ ...criterionBase, source: z.literal('metadata'), key: metadataKeySchema }),
  /** M5. Unknown at calibration time exactly as `extracted` is (RULE-055). */
  z.object({ ...criterionBase, source: z.literal('transcript'), field_key: z.string().min(1) }),
])
export type Criterion = z.infer<typeof criterionSchema>

/** RULE-055: these are unknown until someone writes or says something — and a
 *  `derived` fit until a submission exists to measure — so the calibration bench
 *  reports them as a stated range, never a silent zero. */
export const isUnknownAtCalibration = (criterion: Criterion): boolean =>
  criterion.source === 'extracted' || criterion.source === 'derived' || criterion.source === 'transcript'

/* ── disqualifiers ──────────────────────────────────────────────────────── */

/**
 * A hard gate. RULE-027: a disqualified lead is still stored, still banded and
 * still routed — the disqualifier short-circuits SCORING, not the lead.
 * There is deliberately no band field here: a disqualified lead scores zero
 * and lands in whichever band contains zero, which the ladder guarantees
 * exists.
 */
export const disqualifierSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1).max(RUBRIC_LABEL_MAX),
  when: conditionSchema,
  /** Shown on the outcome page and stored as the verdict reason. */
  reason: z.string().max(500).optional(),
})
export type Disqualifier = z.infer<typeof disqualifierSchema>

/* ── bands ──────────────────────────────────────────────────────────────── */

/**
 * RULE-002: "the band ladder contains no red. Nurture → review → hot runs grey
 * → amber → green." The tone is a TOKEN NAME, not a colour — PRD §3.2 calls
 * this field `colour`, but a hex here would be a design value living outside
 * `design/tokens.css`, which CLAUDE.md forbids. `error` is absent from this
 * enum by construction, so no band can ever be red.
 */
export const BAND_TONES = ['nurture', 'review', 'hot'] as const
export const bandToneSchema = z.enum(BAND_TONES)
export type BandTone = z.infer<typeof bandToneSchema>

export const bandSchema = z.object({
  id: z.string().min(1),
  /** The operator's word for it — "Hot", "Exceptional", "Not right now". */
  label: z.string().min(1).max(100),
  /** Inclusive, integer. */
  min: z.number().int().min(0),
  max: z.number().int().min(0),
  tone: bandToneSchema,
  is_qualified: z.boolean(),
})
export type Band = z.infer<typeof bandSchema>

/* ── the rubric ─────────────────────────────────────────────────────────── */

/** How long an ICP description may be. The Qualify tab states it: "412 / 4,000". */
export const ICP_PROFILE_MAX = 4000

/**
 * PRD §3.8 — "Confidence-gated escalation: above threshold → autonomous decision;
 * below threshold → review queue regardless of computed score; threshold set per
 * funnel." A whole percent. The default is the one value the design draws
 * (`design/screens/editor-qualify.html`, 70%), and nothing reads the field until
 * Story 22.2 — Q-104 carries whether escalation should be on by default at all.
 */
export const CONFIDENCE_THRESHOLD_DEFAULT = 70

export const rubricSchema = z
  .object({
    criteria: z.array(criterionSchema).max(100),
    extraction: z.array(extractionFieldSchema).max(EXTRACTION_FIELD_LIMIT).default([]),
    /** PRD §3.2: natural-language ICP description. Context for the M2 model
     *  and for the M3 one-shot author; never itself scored. The 0–100 fit a
     *  model measures against it reaches the score only through a `derived`
     *  criterion's points (DECISION-138, Story 20.9). */
    icp_profile: z.string().max(ICP_PROFILE_MAX).default(''),
    /**
     * Story 20.4. Beside the bands it decides between, and versioned with them:
     * `putDraft` stores the parsed funnel and publishing snapshots it. Read by
     * Story 22.2.
     */
    confidence_threshold: z.number().int().min(0).max(100).default(CONFIDENCE_THRESHOLD_DEFAULT),
    disqualifiers: z.array(disqualifierSchema).max(50).default([]),
    bands: z.array(bandSchema).min(1).max(10),
  })
  .superRefine((rubric, ctx) => {
    duplicates(rubric.criteria.map((c) => c.id)).forEach((id) =>
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['criteria'],
        message: `two criteria share the id "${id}" — ids must be unique`,
      }),
    )
    duplicates(rubric.extraction.map((f) => f.field_key)).forEach((key) =>
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['extraction'],
        message: `two extraction fields share the key "${key}" — keys must be unique`,
      }),
    )
    duplicates(rubric.disqualifiers.map((d) => d.id)).forEach((id) =>
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['disqualifiers'],
        message: `two disqualifiers share the id "${id}" — ids must be unique`,
      }),
    )
    duplicates(rubric.bands.map((b) => b.id)).forEach((id) =>
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['bands'],
        message: `two bands share the id "${id}" — ids must be unique`,
      }),
    )

    const extractionKeys = new Set(rubric.extraction.map((f) => f.field_key))
    rubric.criteria.forEach((criterion, i) => {
      if (criterion.source === 'extracted' && !extractionKeys.has(criterion.field_key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['criteria', i, 'field_key'],
          message: `no extraction field named "${criterion.field_key}" — define it under rubric.extraction, or point the criterion at one that exists`,
        })
      }
      // Story 20.9: the fit is measured against the description, so a criterion
      // that reads it with no description reads nothing — refused the way an
      // extracted criterion naming no field is, beside it.
      if (criterion.source === 'derived' && rubric.icp_profile.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['criteria', i, 'key'],
          message: `"${criterion.label}" scores how closely a lead matches icp_profile, and icp_profile is empty — describe your ideal customer, or remove the criterion`,
        })
      }
      const over = scoringMapOutcomes(criterion.points).filter((p) => p > criterion.weight)
      if (over.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['criteria', i, 'points'],
          message: `"${criterion.label}" can award ${Math.max(...over)} points but its weight is ${criterion.weight} — raise the weight or lower the points`,
        })
      }
    })

    validateBandLadder(rubric.bands, totalWeight(rubric.criteria), ctx)
  })
export type Rubric = z.infer<typeof rubricSchema>
/** The rubric as callers write it — before Zod applies its defaults. */
export type RubricInput = z.input<typeof rubricSchema>

export const totalWeight = (criteria: Criterion[]): number =>
  criteria.reduce((sum, c) => sum + c.weight, 0)

/**
 * Story 2.2 AC: "overlapping and gapped band definitions are rejected at
 * validation time, not silently resolved."
 *
 * The ladder starts at zero because a disqualified lead scores zero and must
 * still land somewhere (RULE-027), and reaches at least the sum of the weights
 * because a perfect answer set must too. It may reach further — an unreachable
 * top is exactly what the calibration bench (Story 2.4) exists to report.
 */
export function validateBandLadder(
  bands: Band[],
  ceiling: number,
  ctx: z.RefinementCtx,
  path: (string | number)[] = ['bands'],
): void {
  const ordered = [...bands].sort((a, b) => a.min - b.min)

  ordered.forEach((band) => {
    if (band.max < band.min) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path,
        message: `band "${band.label}" ends at ${band.max} before it starts at ${band.min}`,
      })
    }
  })

  if (ordered[0] !== undefined && ordered[0].min !== 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path,
      message: `the lowest band starts at ${ordered[0].min}, so a score of 0 falls in no band — a disqualified lead still has to be banded (RULE-027). Start it at 0`,
    })
  }

  for (let i = 1; i < ordered.length; i++) {
    const previous = ordered[i - 1]!
    const band = ordered[i]!
    if (band.min <= previous.max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path,
        message: `bands "${previous.label}" (…${previous.max}) and "${band.label}" (${band.min}…) overlap — a score in the overlap has two bands. Set "${band.label}" to start at ${previous.max + 1}`,
      })
    } else if (band.min > previous.max + 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path,
        message: `nothing covers ${previous.max + 1}–${band.min - 1} between "${previous.label}" and "${band.label}" — a score in the gap has no band. Set "${band.label}" to start at ${previous.max + 1}`,
      })
    }
  }

  const top = ordered[ordered.length - 1]
  if (top !== undefined && top.max < ceiling) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path,
      message: `the highest band ends at ${top.max} but the rubric can award ${ceiling} — a top score would fall in no band. Raise "${top.label}" to ${ceiling}`,
    })
  }
}

function duplicates(values: string[]): string[] {
  const seen = new Set<string>()
  const repeated = new Set<string>()
  for (const value of values) {
    if (seen.has(value)) repeated.add(value)
    seen.add(value)
  }
  return [...repeated]
}
